import { useEffect, useState, useCallback, type ReactNode } from 'react';
import { api, ApiError } from '../lib/api';
import type {
  CampusView, InstitutionView, SubjectView, AcademicClassView, PeriodView, TimetableView,
} from '../lib/types';
import { Modal, ErrorBar, DialogActions } from './InstitutionsPage';
import { hrApi, type Employee } from '../lib/hrApi';

type Tab = 'subjects' | 'classes' | 'timetable';
const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export function AcademicsPage() {
  const [tab, setTab] = useState<Tab>('subjects');
  const [institutions, setInstitutions] = useState<InstitutionView[]>([]);
  const [campuses, setCampuses] = useState<CampusView[]>([]);
  const [institutionId, setInstitutionId] = useState('');

  useEffect(() => {
    Promise.all([
      api.get<InstitutionView[]>('/api/institutions'),
      api.get<CampusView[]>('/api/campuses'),
    ]).then(([i, c]) => {
      // engineering foundations are most relevant to EngineeringCollege, but allow any
      setInstitutions(i); setCampuses(c);
      if (i.length === 1) setInstitutionId(i[0].id);
    }).catch(() => {});
  }, []);

  const campusName = (id: string) => campuses.find((c) => c.id === id)?.name ?? '';

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>Academics</div>
        <h1 className="h-display" style={{ fontSize: 28 }}>Subjects &amp; timetable</h1>
        <p style={{ color: 'var(--ink-2)', marginTop: 4, maxWidth: 640 }}>
          Define subjects and classes, set the daily period structure, and build each
          class’s weekly timetable. Per-period attendance is generated from these grids.
        </p>
      </div>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 20, flexWrap: 'wrap' }}>
        <select className="input" value={institutionId} onChange={(e) => setInstitutionId(e.target.value)} style={{ maxWidth: 320 }}>
          <option value="">Select an institution</option>
          {institutions.map((i) => (
            <option key={i.id} value={i.id}>{i.name}{campusName(i.campusId) ? ` · ${campusName(i.campusId)}` : ''}</option>
          ))}
        </select>
        <div style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
          <TabBtn active={tab === 'subjects'} onClick={() => setTab('subjects')}>Subjects</TabBtn>
          <TabBtn active={tab === 'classes'} onClick={() => setTab('classes')}>Classes</TabBtn>
          <TabBtn active={tab === 'timetable'} onClick={() => setTab('timetable')}>Timetable</TabBtn>
        </div>
      </div>

      {!institutionId ? (
        <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--ink-2)' }}>Select an institution to begin.</div>
      ) : tab === 'subjects' ? <Subjects institutionId={institutionId} kind={kindOf(institutions, institutionId)} />
        : tab === 'classes' ? <Classes institutionId={institutionId} kind={kindOf(institutions, institutionId)} />
        : <Timetable institutionId={institutionId} />}
    </div>
  );
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button className="btn" onClick={onClick} style={{
      padding: '7px 14px', background: active ? 'var(--brand)' : 'var(--surface)',
      color: active ? '#fff' : 'var(--ink-2)', borderColor: active ? 'var(--brand)' : 'var(--line)',
    }}>{children}</button>
  );
}

// ---------------- Subjects ----------------
/**
 * How this institution organises teaching.
 *
 * ⚠ THIS IS WHY THE SUBJECT FORM LOOKED WRONG TO A SCHOOL.
 *
 * Credits and semesters are university machinery (VTU: Program > Branch > Semester > credits).
 * A CBSE school has neither — it has Class > Section > Subject, subjects run the whole year, and
 * nothing carries a credit value. Showing those fields to a principal, with placeholders reading
 * "21MAT31" and "Transform Calculus", says plainly that the product was built for somebody else.
 *
 * The domain still REQUIRES both (Subject.Create enforces semester 1-8), so we do not remove them -
 * we stop asking a school for numbers that mean nothing to it and send sensible constants instead.
 */
type SchoolingModel = 'school' | 'college';

function kindOf(list: InstitutionView[], id: string): SchoolingModel {
  // ⚠ Always the enum NAME, never a number: InstitutionView is built with `i.Type.ToString()`.
  // (I first wrote a numeric check too; the compiler correctly called it dead code.)
  const t = list.find((i) => i.id === id)?.type;

  // ⚠ Anything unrecognised falls back to 'school' — the safer default. Showing a college two
  // extra fields is a far smaller error than showing a school fields that make it doubt the
  // product, which is the exact failure this function exists to prevent.
  return t === 'EngineeringCollege' || t === 'DegreeCollege' ? 'college' : 'school';
}

function Subjects({ institutionId, kind }: { institutionId: string; kind: SchoolingModel }) {
  const isSchool = kind === 'school';
  const [subjects, setSubjects] = useState<SubjectView[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setSubjects(await api.get<SubjectView[]>(`/api/subjects?institutionId=${institutionId}`)); }
    finally { setLoading(false); }
  }, [institutionId]);
  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <button className="btn btn-accent" onClick={() => setAdding(true)}>+ Add subject</button>
      </div>
      {loading ? <p style={{ color: 'var(--ink-3)' }}>Loading…</p> : (
        <div className="card" style={{ overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ borderBottom: '1px solid var(--line)', textAlign: 'left' }}>
              {/* ⚠ Credits and Sem are hidden for a school — every row would read "0" and "1",
                  which is worse than no column: it looks like data the school failed to fill in. */}
              <Th>Code</Th><Th>Name</Th>
              {!isSchool && <><Th align="right">Credits</Th><Th align="right">Sem</Th></>}
            </tr></thead>
            <tbody>
              {subjects.map((s) => (
                <tr key={s.id} style={{ borderBottom: '1px solid var(--line-2)' }}>
                  <Td><span className="mono">{s.code}</span></Td>
                  <Td><strong>{s.name}</strong></Td>
                  {!isSchool && <>
                    <Td align="right"><span className="mono">{s.credits}</span></Td>
                    <Td align="right"><span className="mono">{s.semester}</span></Td>
                  </>}
                </tr>
              ))}
              {subjects.length === 0 && <tr><Td colSpan={isSchool ? 2 : 4} muted>No subjects yet.</Td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {adding && <SubjectDialog institutionId={institutionId} model={kind} onClose={() => setAdding(false)} onDone={() => { setAdding(false); load(); }} />}
    </div>
  );
}

function SubjectDialog({ institutionId, model, onClose, onDone }: {
  institutionId: string; model: SchoolingModel; onClose: () => void; onDone: () => void;
}) {
  const isSchool = model === 'school';
  const [code, setCode] = useState(''); const [name, setName] = useState('');
  // ⚠ A school never sees or sets these. Credits 0 and semester 1 are sent for every school
  // subject because Subject.Create REQUIRES semester 1-8 — the domain rule is a college rule and
  // we satisfy it with a constant rather than making a principal invent a number.
  //
  // Semester must be the SAME constant every time. If it varied, the same "English" created twice
  // would produce two rows that look identical on screen, which reads as a bug.
  const [credits, setCredits] = useState(isSchool ? '0' : '3');
  const [semester, setSemester] = useState('1');
  const [kind, setKind] = useState('Scholastic');
  const [busy, setBusy] = useState(false); const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null); setBusy(true);
    try {
      await api.post('/api/subjects', { institutionId, code: code.trim(), name: name.trim(), credits: Number(credits), semester: Number(semester), kind });
      onDone();
    } catch (e) { setError(e instanceof ApiError ? e.message : 'Could not add the subject.'); }
    finally { setBusy(false); }
  };

  return (
    <Modal title="Add subject" onClose={onClose}>
      {error && <ErrorBar>{error}</ErrorBar>}
      <label className="label">Code</label>
      <input className="input mono" autoFocus value={code} onChange={(e) => setCode(e.target.value)}
        placeholder={isSchool ? 'ENG' : '21MAT31'} style={{ marginBottom: 12 }} />
      <label className="label">Name</label>
      <input className="input" value={name} onChange={(e) => setName(e.target.value)}
        placeholder={isSchool ? 'English' : 'Transform Calculus & Numerical Methods'}
        style={{ marginBottom: 12 }} />

      <label className="label">Kind</label>
      <select className="input" value={kind} onChange={(e) => setKind(e.target.value)}
        style={{ marginBottom: isSchool ? 8 : 12 }}>
        {/* ⚠ Worded for the reader. A CBSE school knows exactly what "graded A to E" means for
            Art or PE; "Co-scholastic" alone is our jargon, not theirs. */}
        <option value="Scholastic">
          {isSchool ? 'Main subject — marks and grades' : 'Scholastic (graded from marks)'}
        </option>
        <option value="CoScholastic">
          {isSchool ? 'Art, PE, Music — grade only (A–E)' : 'Co-scholastic (grade assigned directly)'}
        </option>
      </select>

      {isSchool ? (
        <p style={{ fontSize: 12, color: 'var(--ink-3)', lineHeight: 1.6, marginBottom: 20 }}>
          Add each subject once — it applies to every class that teaches it. You'll assign subjects
          to classes in the timetable.
        </p>
      ) : (
        <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
          <div style={{ flex: 1 }}>
            <label className="label">Credits</label>
            <input className="input mono" type="number" min={0} max={30} value={credits} onChange={(e) => setCredits(e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <label className="label">Semester</label>
            <select className="input" value={semester} onChange={(e) => setSemester(e.target.value)}>
              {[1,2,3,4,5,6,7,8].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        </div>
      )}
      <DialogActions onCancel={onClose} onConfirm={submit} confirmLabel="Add subject" busy={busy} ready={!!code.trim() && !!name.trim()} />
    </Modal>
  );
}

// ---------------- Classes ----------------
function Classes({ institutionId, kind }: { institutionId: string; kind: SchoolingModel }) {
  const isSchool = kind === 'school';
  const [classes, setClasses] = useState<AcademicClassView[]>([]);
  const [assigning, setAssigning] = useState<AcademicClassView | null>(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setClasses(await api.get<AcademicClassView[]>(`/api/classes?institutionId=${institutionId}`)); }
    finally { setLoading(false); }
  }, [institutionId]);
  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <button className="btn btn-accent" onClick={() => setAdding(true)}>+ Add class</button>
      </div>
      {loading ? <p style={{ color: 'var(--ink-3)' }}>Loading…</p> : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
          {classes.map((c) => (
            <div key={c.id} className="card" style={{ padding: 16 }}>
              <div className="h-display" style={{ fontSize: 17 }}>{c.branch}</div>
              {/* ⚠ A batch has no semester. Printing "Semester 0" tells a coaching institute the
                  product wasn't built for them — the domain already knows via isBatch. */}
              <div style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 2 }}>
                {/* ⚠ SCHOOL IS CHECKED FIRST, and the order is load-bearing.
                     `isBatch` is simply `Semester == 0`, and a bulk SQL import bypasses
                     AcademicClass.Create (which would have rejected 0), so a school's classes can
                     legitimately carry Semester 0. Testing isBatch first made every DVS class read
                     "Batch · A" — a coaching-institute word shown to a CBSE school. */}
                {isSchool
                  ? (c.section ? `Section ${c.section}` : '—')
                  : c.isBatch
                    ? (c.section ? `Batch · ${c.section}` : 'Batch')
                    : `Semester ${c.semester} · Section ${c.section}`}
              </div>

              {/* The class teacher — the person a parent asks for by name. */}
              <div style={{
                marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--line, rgba(0,0,0,0.08))',
                display: 'flex', alignItems: 'center', gap: 8,
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="label" style={{ marginBottom: 2 }}>Class teacher</div>
                  {c.classTeacherName ? (
                    <div style={{ fontSize: 13 }}>{c.classTeacherName}</div>
                  ) : (
                    <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>Not assigned</div>
                  )}
                </div>
                <button className="btn" style={{ fontSize: 12, padding: '4px 10px' }}
                  onClick={() => setAssigning(c)}>
                  {c.classTeacherName ? 'Change' : 'Assign'}
                </button>
              </div>
            </div>
          ))}
          {classes.length === 0 && <div className="card" style={{ padding: 24, color: 'var(--ink-3)', gridColumn: '1/-1', textAlign: 'center' }}>No classes yet.</div>}
        </div>
      )}
      {adding && <ClassDialog institutionId={institutionId} model={kind} onClose={() => setAdding(false)} onDone={() => { setAdding(false); load(); }} />}
      {assigning && (
        <ClassTeacherDialog cls={assigning} onClose={() => setAssigning(null)}
          onDone={() => { setAssigning(null); load(); }} />
      )}
    </div>
  );
}

/**
 * Assign the class teacher.
 *
 * ⚠ Lists ACTIVE employees only. An exited employee must not be assignable to a new section — but
 * an already-assigned one still renders by name on the card, because history should not silently
 * rewrite itself when someone leaves.
 */
function ClassTeacherDialog({ cls, onClose, onDone }: {
  cls: AcademicClassView; onClose: () => void; onDone: () => void;
}) {
  const [staff, setStaff] = useState<Employee[]>([]);
  const [picked, setPicked] = useState<string>(cls.classTeacherId ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    hrApi.listEmployees()
      .then((rows) => setStaff(rows.filter((e) => e.status === 'Active')))
      .catch(() => setStaff([]));
  }, []);

  const submit = async () => {
    setError(null); setBusy(true);
    try {
      // ⚠ Empty string means CLEAR, and must be sent as null — "" is not a Guid and the API
      // would reject the whole request.
      await api.put(`/api/classes/${cls.id}/class-teacher`, { employeeId: picked || null });
      onDone();
    } catch (e) { setError(e instanceof ApiError ? e.message : 'Could not save.'); }
    finally { setBusy(false); }
  };

  return (
    <Modal title={`Class teacher — ${cls.label}`} onClose={onClose}>
      {error && <ErrorBar>{error}</ErrorBar>}
      <label className="label">Responsible teacher</label>
      <select className="input" value={picked} onChange={(e) => setPicked(e.target.value)}
        style={{ marginBottom: 8 }}>
        <option value="">— Nobody assigned —</option>
        {staff.map((e) => (
          <option key={e.id} value={e.id}>{e.fullName} · {e.employeeCode}</option>
        ))}
      </select>
      <p style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 20, lineHeight: 1.6 }}>
        This is the person a parent asks for by name, and the name printed on the report card.
      </p>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button className="btn" onClick={onClose}>Cancel</button>
        <button className="btn btn-accent" disabled={busy} onClick={() => void submit()}>
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
    </Modal>
  );
}

function ClassDialog({ institutionId, model, onClose, onDone }: {
  institutionId: string; model: SchoolingModel; onClose: () => void; onDone: () => void;
}) {
  const isSchool = model === 'school';
  // ⚠ "Branch" is the college word. AcademicClass.Branch is the STORED field either way — a school
  // simply puts "Class 1" in it. Renaming the label costs nothing and renaming the column would
  // cost a migration on a live database, so only the label changes.
  const [branch, setBranch] = useState('');
  const [semester, setSemester] = useState('1');
  const [section, setSection] = useState('A');
  const [busy, setBusy] = useState(false); const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null); setBusy(true);
    try {
      await api.post('/api/classes', { institutionId, branch: branch.trim(), semester: Number(semester), section: section.trim() });
      onDone();
    } catch (e) { setError(e instanceof ApiError ? e.message : 'Could not add the class.'); }
    finally { setBusy(false); }
  };

  return (
    <Modal title="Add class" onClose={onClose}>
      {error && <ErrorBar>{error}</ErrorBar>}
      <label className="label">{isSchool ? 'Class' : 'Branch'}</label>
      <input className="input" autoFocus value={branch} onChange={(e) => setBranch(e.target.value)}
        placeholder={isSchool ? 'Class 1' : 'CSE'} style={{ marginBottom: 12 }} />

      {isSchool ? (
        <>
          <label className="label">Section</label>
          <input className="input" value={section} onChange={(e) => setSection(e.target.value)}
            placeholder="A" style={{ marginBottom: 8 }} />
          <p style={{ fontSize: 12, color: 'var(--ink-3)', lineHeight: 1.6, marginBottom: 20 }}>
            One entry per section — add “Class 1” section A, then section B, and so on.
          </p>
        </>
      ) : (
        <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
          <div style={{ flex: 1 }}>
            <label className="label">Semester</label>
            <select className="input" value={semester} onChange={(e) => setSemester(e.target.value)}>
              {[1,2,3,4,5,6,7,8].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label className="label">Section</label>
            <input className="input" value={section} onChange={(e) => setSection(e.target.value)} placeholder="A" />
          </div>
        </div>
      )}
      <DialogActions onCancel={onClose} onConfirm={submit} confirmLabel="Add class" busy={busy} ready={!!branch.trim() && !!section.trim()} />
    </Modal>
  );
}

// ---------------- Timetable ----------------
function Timetable({ institutionId }: { institutionId: string }) {
  const [classes, setClasses] = useState<AcademicClassView[]>([]);
  const [subjects, setSubjects] = useState<SubjectView[]>([]);
  const [periods, setPeriods] = useState<PeriodView[]>([]);
  const [classId, setClassId] = useState('');
  const [grid, setGrid] = useState<TimetableView | null>(null);
  const [loading, setLoading] = useState(false);

  // load classes, subjects, and the period structure for the institution
  useEffect(() => {
    Promise.all([
      api.get<AcademicClassView[]>(`/api/classes?institutionId=${institutionId}`),
      api.get<SubjectView[]>(`/api/subjects?institutionId=${institutionId}`),
      api.get<PeriodView[]>(`/api/periods?institutionId=${institutionId}`),
    ]).then(([c, s, p]) => { setClasses(c); setSubjects(s); setPeriods(p); }).catch(() => {});
  }, [institutionId]);

  const loadGrid = useCallback(async (cid: string) => {
    if (!cid) { setGrid(null); return; }
    setLoading(true);
    try { setGrid(await api.get<TimetableView>(`/api/timetable/class/${cid}`)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadGrid(classId); }, [classId, loadGrid]);

  const teachingPeriods = periods.filter((p) => !p.isBreak && p.periodNumber != null);
  const subjectById = (id: string) => subjects.find((s) => s.id === id);
  const cellSubject = (day: string, period: number) =>
    grid?.slots.find((s) => s.day === day && s.periodNumber === period)?.subjectId ?? '';

  const setCell = async (day: string, period: number, subjectId: string) => {
    if (!grid) return;
    const updated = await api.put<TimetableView>(`/api/timetable/${grid.id}/slot`, {
      day, periodNumber: period, subjectId: subjectId || null,
    });
    setGrid(updated);
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div>
          <label className="label">Class</label>
          <select className="input" value={classId} onChange={(e) => setClassId(e.target.value)} style={{ minWidth: 220 }}>
            <option value="">Select a class</option>
            {classes.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        </div>
      </div>

      {classes.length === 0 && (
        <div className="card" style={{ padding: 24, color: 'var(--ink-2)', textAlign: 'center' }}>
          Add a class first (Classes tab), then build its timetable here.
        </div>
      )}

      {classId && subjects.length === 0 && (
        <div className="pill pill-warn" style={{ padding: '8px 12px', borderRadius: 'var(--r-md)', marginBottom: 12 }}>
          No subjects defined yet — add subjects before assigning them to the grid.
        </div>
      )}

      {loading && <p style={{ color: 'var(--ink-3)' }}>Loading timetable…</p>}

      {grid && !loading && classId && (
        <div className="card" style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--line)' }}>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 12, color: 'var(--ink-2)', textTransform: 'uppercase' }}>Day</th>
                {teachingPeriods.map((p) => (
                  <th key={p.id} style={{ padding: '10px 8px', textAlign: 'center', fontSize: 12, color: 'var(--ink-2)' }}>
                    <div>P{p.periodNumber}</div>
                    <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', fontWeight: 400 }}>{p.startTime}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {WEEKDAYS.map((day) => (
                <tr key={day} style={{ borderBottom: '1px solid var(--line-2)' }}>
                  <td style={{ padding: '8px 12px', fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap' }}>{day.slice(0, 3)}</td>
                  {teachingPeriods.map((p) => {
                    const sid = cellSubject(day, p.periodNumber!);
                    const subj = sid ? subjectById(sid) : null;
                    return (
                      <td key={p.id} style={{ padding: 4 }}>
                        <select
                          value={sid}
                          onChange={(e) => setCell(day, p.periodNumber!, e.target.value)}
                          title={subj ? `${subj.code} — ${subj.name}` : 'Free'}
                          style={{
                            width: '100%', minWidth: 78, padding: '6px 4px', fontSize: 12,
                            border: '1px solid var(--line)', borderRadius: 6,
                            background: sid ? 'color-mix(in srgb, var(--accent) 12%, var(--surface))' : 'var(--surface-2)',
                            color: 'var(--ink)', cursor: 'pointer',
                          }}>
                          <option value="">—</option>
                          {subjects.map((s) => <option key={s.id} value={s.id}>{s.code}</option>)}
                        </select>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {grid && !loading && teachingPeriods.length > 0 && (
        <p style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 10 }}>
          Tip: the period times come from the institution’s period structure. Cells show subject codes; hover to see the full name.
        </p>
      )}
    </div>
  );
}

function Th({ children, align }: { children: ReactNode; align?: 'right' }) {
  return <th style={{ padding: '12px 16px', fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', textTransform: 'uppercase', letterSpacing: '.02em', textAlign: align ?? 'left' }}>{children}</th>;
}
function Td({ children, colSpan, muted, align }: { children: ReactNode; colSpan?: number; muted?: boolean; align?: 'right' }) {
  return <td colSpan={colSpan} style={{ padding: '12px 16px', color: muted ? 'var(--ink-3)' : 'var(--ink)', textAlign: muted ? 'center' : (align ?? 'left') }}>{children}</td>;
}
